import { type ShallowRef, shallowRef, type WatchStopHandle, watch } from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useDeveloperStore } from '@/app/stores/developer';
import { analytics } from '@/services/Analytics';
import { restApi } from '@/services/api';
import { idleWatcher } from '@/services/IdleWatcher';
import { printing } from '@/services/Printing';
import { clientEvents } from '@/services/telemetry/client-events';
import { sentryService } from '@/services/telemetry/sentry';
import { describeError, silently, track } from '@/shared/utils/track-telemetry';

// The print rounds of one visit that can end with failed copies. After the
// last one the visitor goes to the final print-failed screen, without "Print
// again".
export const MAX_PRINT_ATTEMPTS = 3;

export interface PrintingQueueItem {
  aspectRatio?: number | null;
  count?: number;
  error?: unknown;
  failedCopies?: number;
  isError?: boolean;
  isGroupPortrait?: boolean;
  isNotStarted?: boolean;
  isPartial?: boolean;
  isPreloaded?: boolean;
  isPrinted?: boolean;
  isPrinting?: boolean;
  printedCopies?: number;
  printSubstate?: 'retrying' | 'waiting' | null;
  renderKey?: string;
  status?: string;
  url?: string | null;
  uuid: string;
}

interface IdleWatcherPause {
  continue: () => void;
}

interface UsePrintingFlowOptions {
  scrollToNextSlideIfNeeded: () => void;
}

export interface PrintingFlow {
  enter: () => void;
  isRetrying: Readonly<ShallowRef<boolean>>;
  leave: () => void;
  onContinueWithoutPrint: () => void;
  onFixErrorsClick: () => Promise<void>;
}

function normalizedError(error: unknown): Error {
  return error instanceof Error ? error : new Error(describeError(error));
}

function warnTelemetryFailure(context: string, error: unknown): void {
  // biome-ignore lint/suspicious/noConsole: preserve operational print diagnostics.
  console.warn(context, describeError(error));
}

export function usePrintingFlow({
  scrollToNextSlideIfNeeded,
}: UsePrintingFlowOptions): PrintingFlow {
  const developer = useDeveloperStore();
  const isRetrying = shallowRef(false);
  let disposed = false;
  let entered = false;
  let idleWatcherPause: IdleWatcherPause | null = null;
  let stopPrintingProgressWatch: WatchStopHandle | null = null;
  let successRedirectTimeout: ReturnType<typeof setTimeout> | null = null;
  let printStartTime = 0;
  let printedCount = 0;

  function acquireIdleWatcherPause(): void {
    idleWatcherPause ??= idleWatcher.pause();
  }

  function releaseIdleWatcherPause(): void {
    idleWatcherPause?.continue();
    idleWatcherPause = null;
  }

  function captureAndTrackPrintError(
    sourceError: unknown,
    operation: 'launch' | 'retry'
  ): void {
    const error = normalizedError(sourceError);
    track(
      '[Sentry] Print error capture failed:',
      () => {
        sentryService.addBreadcrumb(
          'printing',
          `Print ${operation} failed`,
          {
            error_message: error.message,
            queue_size: printing.queue?.length || 0,
          },
          'error'
        );
        sentryService.captureException(error, {
          tags: {
            print_operation: operation,
            printer_vertical: printing.printerVertical || 'unknown',
            printer_horizontal: printing.printerHorizontal || 'unknown',
          },
          extra: {
            queue_size: printing.queue?.length || 0,
            total_copies: printing.totalCount,
            has_errors: printing.hasErrors,
            is_printing_in_progress: printing.isPrintingInProgress,
            printer_vertical: printing.printerVertical,
            printer_horizontal: printing.printerHorizontal,
            print_via_browser: printing.shouldPrintViaBrowser,
          },
        });
        sentryService.incrementCounter('printing.operation_error', 1, {
          operation,
        });
      },
      warnTelemetryFailure
    );
  }

  function trackPrintErrors(portraitsToPrint: PrintingQueueItem[]): void {
    const failedPortraits =
      portraitsToPrint?.filter(
        (portrait) => portrait.isError || portrait.isPartial
      ) || [];

    for (const portrait of failedPortraits) {
      track(
        '[Sentry] Print error tracking failed:',
        () => {
          const printerUsed = printing.choosePrinter(portrait);
          sentryService.captureException(
            new Error(`Print job failed: ${portrait.error || 'Unknown error'}`),
            {
              tags: {
                print_status: portrait.status,
                printer: printerUsed || 'unknown',
                is_partial: String(portrait.isPartial),
              },
              extra: {
                portrait_uuid: portrait.uuid,
                requested_copies: portrait.count,
                printed_copies: portrait.printedCopies || 0,
                failed_copies: portrait.failedCopies || 0,
                is_group_portrait: portrait.isGroupPortrait,
                aspect_ratio: portrait.aspectRatio,
                printer_vertical: printing.printerVertical,
                printer_horizontal: printing.printerHorizontal,
                error_message: portrait.error,
              },
              level: portrait.isPartial ? 'warning' : 'error',
            }
          );
        },
        warnTelemetryFailure
      );
    }
  }

  function hasNoPrintRoundLeft(): boolean {
    return printing.failedPrintRounds >= MAX_PRINT_ATTEMPTS;
  }

  function showFinalPrintFailure(): void {
    silently(() => {
      sentryService.addBreadcrumb(
        'printing',
        'Last print round failed, print-failed screen shown',
        { failed_rounds: printing.failedPrintRounds },
        'warning'
      );
      sentryService.incrementCounter('printing.rounds_exhausted', 1);
    });
    router.redirectToStep('print-failed');
  }

  // A launch that another launch skipped did not end a round, thus it leaves
  // the decision to that other launch.
  function isLastRoundFailed(): boolean {
    return (
      !(disposed || printing.isLaunching || store.printingInProgress) &&
      printing.hasErrors &&
      hasNoPrintRoundLeft()
    );
  }

  // After the last failed round the screen goes to print-failed and keeps
  // "Print again" hidden until it closes.
  function endRetryRound(): void {
    if (isLastRoundFailed()) {
      showFinalPrintFailure();
      return;
    }
    isRetrying.value = false;
  }

  async function launchPrinting(): Promise<void> {
    if (!store.sessionId) {
      releaseIdleWatcherPause();
      return;
    }

    if (developer.isPrintLockActive) {
      silently(() => {
        sentryService.addBreadcrumb(
          'printing',
          'Print blocked - print lock active',
          { queue_size: printing.queue?.length || 0 },
          'warning'
        );
      });
      releaseIdleWatcherPause();
      return;
    }

    silently(() => {
      sentryService.addBreadcrumb('printing', 'Launching print job', {
        queue_size: printing.queue?.length || 0,
        total_copies: printing.totalCount,
        printer_vertical: printing.printerVertical,
        printer_horizontal: printing.printerHorizontal,
      });
    });

    // A paid "Print again" mounts this screen again: its round is a retry.
    isRetrying.value = printing.failedPrintRounds > 0;
    try {
      await printing.launch();
    } catch (error) {
      captureAndTrackPrintError(error, 'launch');
    } finally {
      if (!(store.printingInProgress || printing.isLaunching)) {
        releaseIdleWatcherPause();
      }
      endRetryRound();
    }
  }

  const onPrintedSingle = (portrait: PrintingQueueItem): void => {
    if (disposed) {
      return;
    }

    track(
      '[Analytics] Print tracking failed:',
      () => {
        analytics.trackIncrement(analytics.METRICS.num_images_printed, {});
        analytics.trackTimeTo(analytics.METRICS.time_printing_printed_some, {});
      },
      warnTelemetryFailure
    );
    scrollToNextSlideIfNeeded();
    printedCount += 1;

    track(
      '[Sentry] Portrait print tracking failed:',
      () => {
        sentryService.addBreadcrumb(
          'printing',
          'Portrait printed successfully',
          {
            portrait_uuid: portrait?.uuid,
            printed_count: printedCount,
            elapsed_ms: Date.now() - printStartTime,
            is_group_portrait: portrait?.isGroupPortrait,
          }
        );
        sentryService.incrementCounter('printing.portrait_printed', 1, {
          is_group_portrait: String(portrait?.isGroupPortrait),
        });
      },
      warnTelemetryFailure
    );
  };

  const onPrintedQueue = (portraitsToPrint: PrintingQueueItem[]): void => {
    if (disposed) {
      return;
    }

    track(
      '[Analytics] Print tracking failed:',
      () => {
        analytics.trackTimeTo(analytics.METRICS.time_printing_initiated, {});
      },
      warnTelemetryFailure
    );

    const successCount =
      portraitsToPrint?.filter((portrait) => portrait.isPrinted)?.length || 0;
    const errorCount =
      portraitsToPrint?.filter(
        (portrait) => portrait.isError || portrait.isPartial
      )?.length || 0;

    track(
      '[Sentry] Print queue tracking failed:',
      () => {
        sentryService.addBreadcrumb('printing', 'Print queue processed', {
          total_portraits: portraitsToPrint?.length || 0,
          success_count: successCount,
          error_count: errorCount,
          has_errors: printing.hasErrors,
        });
        if (errorCount > 0) {
          sentryService.incrementCounter('printing.queue_errors', errorCount);
          trackPrintErrors(portraitsToPrint);
        }
      },
      warnTelemetryFailure
    );
  };

  const onPrintedAllSuccessfully = (): void => {
    if (disposed) {
      return;
    }

    track(
      '[Sentry] Print completion tracking failed:',
      () => {
        analytics.trackTimeTo(analytics.METRICS.time_printing_printed_all, {});
        const totalElapsedMs = Date.now() - printStartTime;
        sentryService.addBreadcrumb(
          'printing',
          'All portraits printed successfully',
          {
            total_printed: printedCount,
            total_time_ms: totalElapsedMs,
            avg_time_per_portrait_ms:
              printedCount > 0 ? Math.round(totalElapsedMs / printedCount) : 0,
          }
        );
        sentryService.trackMetric(
          'printing.completion_time_ms',
          totalElapsedMs,
          'millisecond',
          { portraits_count: String(printedCount) }
        );
        sentryService.incrementCounter('printing.completed_successfully', 1, {
          portraits_count: String(printedCount),
        });
      },
      warnTelemetryFailure
    );

    if (successRedirectTimeout) {
      clearTimeout(successRedirectTimeout);
    }
    successRedirectTimeout = setTimeout(() => {
      if (!disposed) {
        router.redirectToStep('success');
      }
    }, 2000);
  };

  const printingSubscriptions: ReadonlyArray<
    readonly [string, (...args: never[]) => void]
  > = [
    [printing.EVENTS.PRINTED_SINGLE, onPrintedSingle],
    [printing.EVENTS.PRINTED_QUEUE, onPrintedQueue],
    [printing.EVENTS.PRINTED_ALL_SUCCESSFULLY, onPrintedAllSuccessfully],
  ];

  function subscribeToPrinting(): void {
    for (const [event, handler] of printingSubscriptions) {
      printing.on(event, handler);
    }
  }

  function unsubscribeFromPrinting(): void {
    for (const [event, handler] of printingSubscriptions) {
      printing.off(event, handler);
    }
  }

  function recordQueueInitialization(): void {
    const queueSize = store.printingQueue?.length || 0;
    const totalCopies = printing.totalCount;
    track(
      '[Sentry] Print queue initialization tracking failed:',
      () => {
        clientEvents.record(
          'print_queue_initialized',
          {
            queue_size: queueSize,
            total_copies: totalCopies,
            has_errors: printing.hasErrors,
            printer_vertical: printing.printerVertical,
            printer_horizontal: printing.printerHorizontal,
            method: printing.shouldPrintViaBrowser ? 'browser' : 'backend',
          },
          { area: 'printing' }
        );
        sentryService.addBreadcrumb('printing', 'Print step mounted', {
          queue_size: queueSize,
          total_copies: totalCopies,
          has_errors: printing.hasErrors,
        });
        sentryService.incrementCounter('printing.queue_initialized', 1, {
          queue_size: String(queueSize),
          total_copies: String(totalCopies),
        });
      },
      warnTelemetryFailure
    );
  }

  function enter(): void {
    if (entered) {
      return;
    }
    entered = true;
    disposed = false;

    restApi.abortGenerationRequests();
    acquireIdleWatcherPause();
    stopPrintingProgressWatch = watch(
      () => store.printingInProgress,
      (inProgress) => {
        if (disposed) {
          return;
        }
        if (inProgress) {
          acquireIdleWatcherPause();
        } else {
          releaseIdleWatcherPause();
        }
      }
    );
    recordQueueInitialization();
    printStartTime = Date.now();
    printedCount = 0;

    // A print service may emit synchronously, so all handlers must exist first.
    subscribeToPrinting();
    void launchPrinting();

    track(
      '[Analytics] Print tracking failed:',
      () => {
        analytics.trackTimeTo(analytics.METRICS.time_printing_confirmed, {});
        analytics.trackTimeTo(analytics.METRICS.time_printing_initiated, {});
      },
      warnTelemetryFailure
    );
  }

  function leave(): void {
    if (disposed) {
      return;
    }
    disposed = true;
    stopPrintingProgressWatch?.();
    stopPrintingProgressWatch = null;
    unsubscribeFromPrinting();
    if (successRedirectTimeout) {
      clearTimeout(successRedirectTimeout);
      successRedirectTimeout = null;
    }
    releaseIdleWatcherPause();
  }

  async function onFixErrorsClick(): Promise<void> {
    if (!store.sessionId || disposed || isRetrying.value) {
      return;
    }
    // A screen that mounted during the last round did not see its end, thus
    // its button can still show.
    if (hasNoPrintRoundLeft()) {
      showFinalPrintFailure();
      return;
    }
    if (developer.isPrintLockActive) {
      silently(() => {
        sentryService.addBreadcrumb(
          'printing',
          'Retry blocked - print lock active',
          {},
          'warning'
        );
      });
      return;
    }

    // "Print again" prints only the copies with a final answer without a
    // print, under new keys. A refunded copy never prints for free: it first
    // takes a new payment of the amount that came back. A copy without a
    // final answer can still print under its key, thus it does not go again.
    const reprintOrder = printing.startReprintOrder();
    if (reprintOrder) {
      silently(() => {
        sentryService.addBreadcrumb('printing', 'Reprint needs a new payment', {
          amount_cents: reprintOrder.amountCents,
        });
      });
      router.redirectToStep('payment-terminal');
      return;
    }

    isRetrying.value = true;
    acquireIdleWatcherPause();
    const erroredPortraits = printing.query({ error: true });
    printStartTime = Date.now();
    silently(() => {
      sentryService.addBreadcrumb('printing', 'Retrying failed print jobs', {
        error_count: erroredPortraits?.length || 0,
        portrait_uuids: erroredPortraits?.map(
          (portrait: PrintingQueueItem) => portrait.uuid
        ),
      });
    });

    try {
      await printing.launch({ fix: true });
    } catch (error) {
      captureAndTrackPrintError(error, 'retry');
    } finally {
      if (!(store.printingInProgress || printing.isLaunching)) {
        releaseIdleWatcherPause();
      }
      endRetryRound();
    }
  }

  function onContinueWithoutPrint(): void {
    if (disposed) {
      return;
    }
    const errorCount = printing.query({ error: true })?.length || 0;
    silently(() => {
      sentryService.addBreadcrumb(
        'printing',
        'Customer left the print-error screen after failures',
        { error_count: errorCount, printed_count: printedCount }
      );
      sentryService.incrementCounter('printing.continued_after_failure', 1, {
        error_count: String(errorCount),
      });
    });
    router.redirectToStep('print-failed');
  }

  return {
    enter,
    isRetrying,
    leave,
    onContinueWithoutPrint,
    onFixErrorsClick,
  };
}
